import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { BillingPlanResponse } from "../../models/ResponseModels/BillingPlanResponse";
import {
  createCheckoutSessionRequest,
  getBillingPlansRequest,
} from "../../api/billingApi";

interface BillingState {
  plans: BillingPlanResponse[];
  loading: boolean;
  error: string | null;
}

const initialState: BillingState = {
  plans: [],
  loading: false,
  error: null,
};

export const fetchBillingPlans = createAsyncThunk(
  "billing/fetchBillingPlans",
  async (_, { rejectWithValue }) => {
    try {
      return await getBillingPlansRequest();
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to load billing plans.",
      );
    }
  },
);

export const startCheckout = createAsyncThunk(
  "billing/startCheckout",
  async (subscriptionPriceId: number, { rejectWithValue }) => {
    try {
      const response = await createCheckoutSessionRequest(subscriptionPriceId);

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return true;
      }

      if (response.payment) {
        const { submitMonriForm } =
          await import("../../utils/helperFunctions/submitMonriForm");

        submitMonriForm(response.payment.action, response.payment.fields);
        return true;
      }

      return rejectWithValue("Checkout response is missing payment data.");
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.message || "Failed to start checkout.",
      );
    }
  },
);

const billingSlice = createSlice({
  name: "billing",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBillingPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBillingPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = Array.isArray(action.payload.plans)
          ? action.payload.plans
          : [];
      })
      .addCase(fetchBillingPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(startCheckout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(startCheckout.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(startCheckout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default billingSlice.reducer;
